import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  VStack,
  Text,
  Heading,
  Spinner,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
} from '@chakra-ui/react';
import { getOidcUserManager } from '@/services/oidcClient';
import { apiClient, ApiError } from '@/services/apiClient';

interface CompleteOAuthResponse {
  isNewUser: boolean;
  organization?: { id: string };
}

/**
 * OIDC redirect target. Exchanges the authorization code for tokens via the
 * UserManager, then completes the backend organization-creation handshake
 * (POST /api/v1/auth/complete-oauth). Brand-new users are prompted for an
 * organization name; returning users are sent to the root route (or wherever
 * the caller stashed in OIDC state).
 */
export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsOrgName, setNeedsOrgName] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  // Returning users land on the app home (/app), not "/" — which is now the
  // public marketing page — unless the caller stashed a destination in OIDC state.
  const [redirectTo, setRedirectTo] = useState<string>('/app');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mgr = await getOidcUserManager();
        const user = await mgr.signinRedirectCallback();
        if (cancelled) return;

        // Extract the destination from OIDC state before any React state update —
        // setRedirectTo is async, so passing the value directly avoids seeing the
        // stale initial '/' on the first tryCompleteOAuth call.
        const fromState = (user.state as { from?: string } | undefined)?.from;
        if (fromState) setRedirectTo(fromState);

        await tryCompleteOAuth('', fromState);
      } catch (err) {
        if (cancelled) return;
        console.warn('[OAuthCallback] failed', err);
        setError('Authentication failed. Please try again.');
        setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryCompleteOAuth = async (orgName: string, destination?: string) => {
    // Use the explicitly-passed destination when available (first call from the
    // effect), otherwise fall back to the redirectTo state (org-name form path).
    const dest = destination ?? redirectTo;
    try {
      const result = await apiClient.post<CompleteOAuthResponse>(
        '/api/v1/auth/complete-oauth',
        { organizationName: orgName },
      );
      if (result.isNewUser && !orgName) {
        setNeedsOrgName(true);
        setIsLoading(false);
        return;
      }
      navigate(result.isNewUser ? '/onboarding' : dest, { replace: true });
    } catch (err) {
      // The backend signals "missing org name" with a validation error code;
      // surface the org-name prompt rather than dumping a stack trace.
      if (err instanceof ApiError && err.status === 400) {
        setNeedsOrgName(true);
        setIsLoading(false);
        return;
      }
      throw err;
    }
  };

  const handleSubmitOrgName = async () => {
    if (!organizationName.trim()) {
      setError('Organization name is required');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await tryCompleteOAuth(organizationName.trim());
    } catch (err) {
      console.error('Complete OAuth error:', err);
      setError('Failed to complete sign-up. Please try again.');
      setIsLoading(false);
    }
  };

  if (needsOrgName) {
    return (
      <Box flex={1} display="flex" justifyContent="center" alignItems="center" p={4} bg="gray.50">
        <VStack spacing={4} p={6} bg="white" borderRadius="lg" w="100%" maxW="480px" boxShadow="md">
          <VStack spacing={2} align="center">
            <Heading textAlign="center">Welcome! 🎉</Heading>
            <Text textAlign="center" color="gray.500">
              Let's set up your organization
            </Text>
          </VStack>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <FormControl>
            <FormLabel htmlFor="organizationName">Organization Name</FormLabel>
            <Input
              id="organizationName"
              placeholder="My Company"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
            />
            <FormHelperText>This is the name of your business or team</FormHelperText>
          </FormControl>

          <Button
            onClick={handleSubmitOrgName}
            isDisabled={isLoading || !organizationName.trim()}
            colorScheme="blue"
            w="100%"
          >
            {isLoading ? <Spinner color="white" size="sm" /> : 'Continue'}
          </Button>
        </VStack>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <VStack flex={1} justify="center" align="center" spacing={4}>
        <Spinner size="xl" color="blue.500" />
        <Text color="gray.500">Completing sign-in...</Text>
      </VStack>
    );
  }

  if (error) {
    return (
      <Box flex={1} display="flex" justifyContent="center" alignItems="center" p={4} bg="gray.50">
        <VStack spacing={4} p={6} bg="white" borderRadius="lg" w="100%" maxW="480px" align="center">
          <Text fontSize="48px">❌</Text>
          <Heading textAlign="center">Authentication Failed</Heading>
          <Text textAlign="center" color="gray.500">{error}</Text>
          <Button onClick={() => navigate('/signup')} colorScheme="blue">
            Try Again
          </Button>
        </VStack>
      </Box>
    );
  }

  return null;
}

export default OAuthCallbackPage;
